import React, { useCallback, useContext, useMemo, useState } from 'react';
import Modal from '@atlaskit/modal-dialog';

import AccountPickerContext from 'contexts/AccountPickerContext';
import CurrentUserContext from 'contexts/CurrentUserContext';
import ToastContext from 'contexts/ToastContext';
import submitIssueReport, {
  IssueReportPriority,
  IssueReportType,
} from 'api/submitIssueReport';
import Form from 'kingpin/forms/Form';
import FormHeader from 'kingpin/forms/FormHeader';
import FormContent from 'kingpin/forms/FormContent';
import FormInput from 'kingpin/forms/FormInput';
import TextInput from 'kingpin/atoms/TextInput';
import Button from 'kingpin/atoms/Button';
import Row from 'components/Common/Row';
import TextArea from 'components/Inputs/TextArea';
import Dropdown from 'components/Inputs/Dropdown';

import AttachmentsPicker from './AttachmentsPicker';
import useReportAnIssueFormState from './useReportAnIssueFormState';

const PRIORITY_LABELS: Record<IssueReportPriority, string> = {
  '': 'None',
  low: 'Low',
  medium: 'Medium',
  high: 'High',
};

const ReportAnIssueModal = ({
  type,
  gadgetName,
  close,
}: {
  type: IssueReportType;
  gadgetName?: string;
  close: () => void;
}) => {
  const { selectedAccountId } = useContext(AccountPickerContext);
  const { email: userEmail } = useContext(CurrentUserContext);
  const { showToast } = useContext(ToastContext);

  const {
    title,
    onTitleChanged,
    description,
    onDescriptionChanged,
    priority,
    setPriority,
    files,
    setFiles,
    draft,
  } = useReportAnIssueFormState();

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const priorityOptions = useMemo<DropdownOption[]>(
    () =>
      (['', 'low', 'medium', 'high'] as IssueReportPriority[]).map((value) => ({
        key: value === '' ? 'none' : value,
        label: PRIORITY_LABELS[value],
        value,
        isSelected: priority === value,
        onSelected: () => setPriority(value),
      })),
    [priority, setPriority],
  );

  const onSubmit = useCallback(async () => {
    if (!draft || isSubmitting) {
      return;
    }

    setIsSubmitting(true);
    const sourceUrl = `${window.location.origin}${window.location.pathname}?accountId=${selectedAccountId}`;

    try {
      const ok = await submitIssueReport({
        type,
        title: draft.title,
        description: draft.description,
        priority: draft.priority,
        files: draft.files,
        sourceUrl,
        accountId: selectedAccountId,
        userEmail: userEmail || '',
        gadgetName,
      });
      if (ok) {
        showToast('Issue reported. Thanks!');
        close();
      } else {
        showToast('Could not submit issue. Please try again.');
      }
    } catch {
      showToast('Could not submit issue. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  }, [
    draft,
    isSubmitting,
    type,
    selectedAccountId,
    userEmail,
    gadgetName,
    showToast,
    close,
  ]);

  return (
    <Modal onClose={close} shouldScrollInViewport={false} autoFocus={false}>
      <Form>
        <FormHeader title="Report an issue" onClose={close} />
        <FormContent>
          <FormInput label="Title">
            <TextInput
              value={title}
              onChange={onTitleChanged}
              placeholder="Short summary of the issue"
              data-testid="report-issue-title"
            />
          </FormInput>
          <FormInput label="Description">
            <TextArea
              value={description}
              onChange={onDescriptionChanged}
              placeholder="What went wrong? Steps to reproduce, expected vs. actual behaviour."
              data-testid="report-issue-description"
            />
          </FormInput>
          <FormInput label="Priority">
            <Dropdown
              options={priorityOptions}
              selectedLabel={PRIORITY_LABELS[priority]}
              testId="report-issue-priority"
            />
          </FormInput>
          <FormInput label="Attachments">
            <AttachmentsPicker files={files} setFiles={setFiles} />
          </FormInput>
        </FormContent>
        <Row spaceBetween>
          <Button
            size="Small"
            type="Secondary"
            label="Cancel"
            onClick={close}
            isDisabled={isSubmitting}
          />
          <Button
            size="Small"
            type="Primary"
            label="Submit"
            onClick={onSubmit}
            isLoading={isSubmitting}
            isDisabled={draft === undefined || isSubmitting}
            testId="report-issue-submit"
          />
        </Row>
      </Form>
    </Modal>
  );
};

export default ReportAnIssueModal;
